<script setup lang="ts">
/**
 * The end of a paged list: „12 von 48“ and a „Weitere laden“ button. With
 * `auto` the next page also loads when the block scrolls into view (the
 * button stays for keyboard users and as the fallback without
 * IntersectionObserver). A failed load says so and offers a retry; once
 * everything is in it reads „Alle 48 geladen“.
 *
 * Bind the loader with `@load` — it may return a promise, and the block
 * shows its own loading state until it settles. Loaders that report through
 * a store instead pass `loading` and `error`; the block then waits for
 * `loading` to drop before it looks again.
 */
import { computed, inject, ref, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import { useNxInfiniteScroll } from '../Composables/useNxInfiniteScroll';
import { i18nInjectionKey } from '../internal';

/** A stuck `loading` flag must not keep the auto-loader waiting forever. */
const LOADING_WAIT_MS = 10_000;

const props = withDefaults(
  defineProps<{
    /** Rows shown so far. */
    loaded?: number | null;
    /** All rows, when the server says; unknown totals read „12 geladen“. */
    total?: number | null;
    /** Whether another page exists. Default: `loaded < total`, or true without a total. */
    hasMore?: boolean | null;
    /** Rows one press brings: „Weitere 25 laden“. */
    pageSize?: number | null;
    /** Load on scroll as well as on the button. */
    auto?: boolean;
    /** How early `auto` starts. Default `'200px'`. */
    rootMargin?: string;
    /** Set by a loader that reports through a store. */
    loading?: boolean;
    /** Set by a loader that reports through a store; a string replaces the message. */
    error?: boolean | string | null;
    /** Called to load the next page (`@load`). */
    onLoad?: () => unknown;
    showEnd?: boolean;
    label?: string;
    loadingLabel?: string;
    errorLabel?: string;
    retryLabel?: string;
    /** Overrides the count text. */
    countLabel?: string;
    locale?: string;
  }>(),
  {
    loaded: null,
    total: null,
    hasMore: null,
    pageSize: null,
    auto: false,
    rootMargin: '200px',
    loading: false,
    error: null,
    onLoad: undefined,
    showEnd: true,
    label: 'Weitere laden',
    loadingLabel: 'Lädt …',
    errorLabel: 'Laden fehlgeschlagen',
    retryLabel: 'Erneut versuchen',
    countLabel: undefined,
    locale: undefined,
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const fmt = (n: number) => new Intl.NumberFormat(locale.value).format(n);
const known = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n);

const more = computed(() => {
  if (typeof props.hasMore === 'boolean') return props.hasMore;
  if (known(props.total) && known(props.loaded)) return props.loaded < props.total;
  return true;
});

const running = ref(false);
const failure = ref<unknown>(null);
const busy = computed(() => props.loading || running.value);
const failed = computed(() => !busy.value && (!!props.error || failure.value !== null));

const countText = computed(() => {
  if (props.countLabel !== undefined) return props.countLabel;
  if (!known(props.loaded)) return '';
  if (known(props.total)) return `${fmt(props.loaded)} von ${fmt(props.total)}`;
  return `${fmt(props.loaded)} geladen`;
});

const buttonText = computed(() => {
  if (!known(props.pageSize) || props.pageSize <= 0) return props.label;
  const rest =
    known(props.total) && known(props.loaded)
      ? Math.min(props.pageSize, Math.max(0, props.total - props.loaded))
      : props.pageSize;
  return rest > 0 ? `Weitere ${fmt(rest)} laden` : props.label;
});

const endText = computed(() => {
  if (!props.showEnd || more.value) return '';
  if (known(props.total)) {
    return props.total === 0 ? '' : `Alle ${fmt(props.total)} geladen`;
  }
  return known(props.loaded) && props.loaded > 0 ? 'Alle geladen' : '';
});

const errorText = computed(() =>
  typeof props.error === 'string' && props.error ? props.error : props.errorLabel
);

function untilIdle(): Promise<void> {
  if (!props.loading) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(done, LOADING_WAIT_MS);
    const stop = watch(
      () => props.loading,
      (loading) => {
        if (!loading) done();
      }
    );
    function done() {
      clearTimeout(timer);
      stop();
      resolve();
    }
  });
}

/** Runs the loader; rejects on failure so the auto-loader backs off. */
async function run(): Promise<void> {
  if (busy.value) return;
  running.value = true;
  failure.value = null;
  try {
    await props.onLoad?.();
    await untilIdle();
  } catch (err) {
    failure.value = err;
    throw err;
  } finally {
    running.value = false;
  }
  if (props.error) throw new Error(errorText.value);
}

function press() {
  if (busy.value) return;
  run().catch(() => {
    // Shown through `failed`; nothing else to do.
  });
}

const root = useTemplateRef<HTMLElement>('root');
useNxInfiniteScroll(root, run, {
  hasMore: () => props.auto && more.value,
  disabled: () => busy.value || !!props.error,
  rootMargin: props.rootMargin,
});
</script>

<template>
  <div
    v-if="more || failed || countText || endText"
    ref="root"
    class="nx-load-more"
    :aria-busy="busy || undefined"
    data-testid="nx-load-more"
  >
    <span v-if="countText" class="nx-load-more__count" data-testid="nx-load-more-count">
      {{ countText }}
    </span>
    <template v-if="failed">
      <span class="nx-load-more__error" role="alert">{{ errorText }}</span>
      <NxButton height="s" variant="outline" data-testid="nx-load-more-retry" @click="press">
        {{ props.retryLabel }}
      </NxButton>
    </template>
    <NxButton
      v-else-if="more"
      height="s"
      variant="outline"
      class="nx-load-more__button"
      :aria-disabled="busy || undefined"
      data-testid="nx-load-more-button"
      @click="press"
    >
      <span v-if="busy" class="nx-load-more__spinner" aria-hidden="true" />
      {{ busy ? props.loadingLabel : buttonText }}
    </NxButton>
    <span v-else-if="endText" class="nx-load-more__end" data-testid="nx-load-more-end">
      {{ endText }}
    </span>
    <span class="nx-load-more__status" role="status">{{ busy ? props.loadingLabel : '' }}</span>
  </div>
</template>

<style scoped>
.nx-load-more {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs) var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  color: var(--nxt-text-muted-fg);
  font-size: var(--nxt-font-size-sm);
}
.nx-load-more__count {
  font-variant-numeric: tabular-nums;
}
.nx-load-more__error {
  color: var(--nxt-danger-subtle-fg);
}
.nx-load-more__end {
  color: var(--nxt-text-subtle-fg);
}
.nx-load-more__button[aria-disabled='true'] {
  cursor: progress;
}
.nx-load-more__spinner {
  flex: 0 0 auto;
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-border);
  border-top-color: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-full);
  animation: nx-load-more-spin 0.8s linear infinite;
}
.nx-load-more__status {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
@keyframes nx-load-more-spin {
  to {
    transform: rotate(1turn);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-load-more__spinner {
    animation-duration: 3s;
  }
}
</style>
